/**
 * Target Catalog & Metadata Definitions for Red Teaming.
 *
 * Defines supported AI systems, model providers, local model runtimes,
 * connection methods, and application targets with category memberships.
 */

export interface Authentication {
  type: string;
  key?: string;
  value?: string;
  location?: string;
  token?: string;
  username?: string;
  password?: string;
}

export interface Target {
  id: number;
  name: string;
  type: string;
  endpoint: string;
  authentication: Authentication;
  headers: Record<string, string>;
  timeout: number;
  description: string;
  method?: string;
  body_template?: string;
  response_path?: string;
  input_key?: string;
  response_key?: string;
  protocols?: string[];
  app_id?: string;
  created_at: string;
  updated_at: string;
  lastStatus?: "Connected" | "Failed" | "Untested";
  lastTestedTime?: string;
  last_scan_id?: string;
  last_scan_score?: number;
  last_scan_status?: string;
  last_scan_date?: string;
  last_scan_summary?: Record<string, unknown> | unknown;
}

export type TargetCategoryId =
  | "all"
  | "my-applications"
  | "agent-frameworks"
  | "ai-providers"
  | "local-models";

export interface TargetCategoryMeta {
  id: TargetCategoryId;
  label: string;
}

export const TARGET_CATEGORIES: TargetCategoryMeta[] = [
  { id: "all", label: "All" },
  { id: "my-applications", label: "My Application" },
  { id: "agent-frameworks", label: "Agent Frameworks" },
  { id: "ai-providers", label: "AI Providers" },
  { id: "local-models", label: "Local Models" },
];

export type TargetBadgeType = "Popular" | "Local" | "Agent" | "My Application";

export interface TargetCatalogItem {
  id: string;
  name: string;
  description: string;
  badge?: TargetBadgeType;
  categories: TargetCategoryId[];
  searchKeywords: string[];
  iconType: string;

  // Defaults for target setup
  defaultTargetType: "http" | "websocket" | "langchain" | "traces" | "ai_provider" | "python" | "javascript" | "go" | "a2a" | "claude-agent" | "mcp" | "open-interpreter" | string;
  defaultModelProvider?: string;
  defaultModelName?: string;
  defaultModelBaseUrl?: string;
  defaultEndpoint?: string;
  defaultAuthType?: "none" | "api_key" | "bearer" | "basic";
  defaultApiKeyLoc?: "header" | "query";
  defaultApiKeyName?: string;
  defaultHeaders?: Record<string, string>;
  defaultBodyTemplate?: string;
  defaultResponsePath?: string;
  defaultAppTestingMode?: "application" | "model";
}

/**
 * Built-in catalog of unique supported target integrations.
 * Matches the 55 target architectures defined in the Enterprise Security specification:
 * - 8 Application targets (My Application)
 * - 14 Agent Frameworks
 * - 27 AI Providers
 * - 6 Local Models
 * Total: 55 targets
 */
export const TARGET_CATALOG: TargetCatalogItem[] = [
  // ==========================================
  // 1. My Application (8 Targets)
  // ==========================================
  {
    id: "http",
    name: "HTTP/HTTPS Endpoint",
    description: "Connect to your REST API or HTTP endpoint",
    badge: "Popular",
    categories: ["all", "my-applications"],
    searchKeywords: ["http", "https", "rest", "api", "endpoint", "curl", "web", "application"],
    iconType: "http",
    defaultTargetType: "http",
    defaultEndpoint: "https://api.example.com/v1/chat",
    defaultAuthType: "none",
    defaultBodyTemplate: '{\n  "prompt": "{{prompt}}"\n}',
    defaultResponsePath: "choices[0].message.content",
    defaultAppTestingMode: "application",
  },
  {
    id: "python",
    name: "Custom Python Agent",
    description: "Custom Python provider conforming to call_api contract",
    badge: "Popular",
    categories: ["all", "my-applications", "agent-frameworks"],
    searchKeywords: ["python", "py", "script", "provider", "call_api", "custom", "agent"],
    iconType: "python",
    defaultTargetType: "python",
    defaultEndpoint: "file://./custom_python_agent.py",
    defaultAuthType: "none",
    defaultAppTestingMode: "application",
  },
  {
    id: "javascript",
    name: "JavaScript / TypeScript",
    description: "Custom JS/TS script or integration",
    badge: "Popular",
    categories: ["all", "my-applications"],
    searchKeywords: ["javascript", "typescript", "js", "ts", "node", "express", "nextjs"],
    iconType: "javascript",
    defaultTargetType: "javascript",
    defaultEndpoint: "http://localhost:3000/api/chat",
    defaultAuthType: "none",
    defaultBodyTemplate: '{\n  "message": "{{prompt}}"\n}',
    defaultResponsePath: "reply",
    defaultAppTestingMode: "application",
  },
  {
    id: "browser-automation",
    name: "Browser Automation",
    description: "Test web apps via browser automation",
    categories: ["all", "my-applications"],
    searchKeywords: ["browser", "automation", "playwright", "puppeteer", "selenium", "web"],
    iconType: "browser",
    defaultTargetType: "http",
    defaultEndpoint: "http://localhost:3000",
    defaultAuthType: "none",
    defaultAppTestingMode: "application",
  },
  {
    id: "go",
    name: "Go",
    description: "Custom Go application or service",
    categories: ["all", "my-applications"],
    searchKeywords: ["go", "golang", "service", "microservice", "gin", "fiber"],
    iconType: "go",
    defaultTargetType: "go",
    defaultEndpoint: "http://localhost:8080/api/chat",
    defaultAuthType: "none",
    defaultBodyTemplate: '{\n  "query": "{{prompt}}"\n}',
    defaultResponsePath: "answer",
    defaultAppTestingMode: "application",
  },
  {
    id: "shell",
    name: "Shell Command",
    description: "Execute shell commands or scripts",
    categories: ["all", "my-applications"],
    searchKeywords: ["shell", "bash", "sh", "cli", "command", "terminal", "script", "exec", "executable"],
    iconType: "shell",
    defaultTargetType: "shell",
    defaultEndpoint: "exec:python /path/to/agent.py",
    defaultAuthType: "none",
    defaultAppTestingMode: "application",
  },
  {
    id: "websocket",
    name: "WebSocket",
    description: "Test live WebSocket (ws:// / wss://)",
    categories: ["all", "my-applications"],
    searchKeywords: ["websocket", "ws", "wss", "realtime", "socket", "live", "duplex"],
    iconType: "websocket",
    defaultTargetType: "websocket",
    defaultEndpoint: "wss://api.example.com/ws",
    defaultResponsePath: "response",
    defaultAppTestingMode: "application",
  },
  {
    id: "custom",
    name: "Custom Target",
    description: "Custom target connection or provider",
    categories: ["all", "my-applications"],
    searchKeywords: ["custom", "provider", "target", "adapter", "plugin", "other"],
    iconType: "custom",
    defaultTargetType: "custom",
    defaultEndpoint: "http://localhost:8000/api",
    defaultAuthType: "none",
    defaultAppTestingMode: "application",
  },

  // ==========================================
  // 2. Agent Frameworks (14 Targets)
  // ==========================================
  {
    id: "a2a",
    name: "A2A Agent",
    description: "Agent-to-Agent communication protocols",
    categories: ["all", "agent-frameworks"],
    searchKeywords: ["a2a", "agent", "multi-agent", "protocol"],
    iconType: "agent",
    defaultTargetType: "a2a",
    defaultEndpoint: "http://localhost:8000/agent/a2a",
    defaultAppTestingMode: "application",
  },
  {
    id: "claude-agent",
    name: "Claude Agent SDK",
    description: "Test and red-team agents built using Anthropic's Claude Agent SDK.",
    badge: "Agent",
    categories: ["all", "agent-frameworks"],
    searchKeywords: ["claude", "agent", "sdk", "anthropic", "claude-code", "claude-agent-sdk"],
    iconType: "anthropic",
    defaultTargetType: "claude-agent",
    defaultEndpoint: "http://localhost:8000/agent/claude",
    defaultAppTestingMode: "application",
  },
  {
    id: "openai-agents",
    name: "OpenAI Agents SDK",
    description: "OpenAI Agents SDK multi-agent framework and runner workflows",
    badge: "Agent",
    categories: ["all", "agent-frameworks"],
    searchKeywords: ["openai", "agents", "sdk", "runner", "multi-agent", "swarm", "openai-agents"],
    iconType: "openai",
    defaultTargetType: "openai-agents",
    defaultEndpoint: "file://./openai_agent.py",
    defaultAuthType: "none",
    defaultAppTestingMode: "application",
  },
  {
    id: "autogen",
    name: "AutoGen Agent",
    description: "Microsoft AutoGen multi-agent conversational framework",
    badge: "Agent",
    categories: ["all", "agent-frameworks"],
    searchKeywords: ["autogen", "microsoft", "multi-agent", "conversation", "pyautogen"],
    iconType: "agent",
    defaultTargetType: "autogen",
    defaultEndpoint: "file://./autogen_agent.py",
    defaultAuthType: "none",
    defaultAppTestingMode: "application",
  },
  {
    id: "bedrock-agents",
    name: "AWS Bedrock Agents",
    description: "Amazon Bedrock managed AI agents",
    categories: ["all", "agent-frameworks"],
    searchKeywords: ["aws", "bedrock", "agents", "amazon", "bedrock-agent"],
    iconType: "aws",
    defaultTargetType: "bedrock-agent",
    defaultEndpoint: "https://bedrock-agent-runtime.us-east-1.amazonaws.com",
    defaultAppTestingMode: "application",
  },
  {
    id: "crewai",
    name: "CrewAI Agent",
    description: "Role-playing, autonomous multi-agent orchestration framework",
    badge: "Agent",
    categories: ["all", "agent-frameworks"],
    searchKeywords: ["crewai", "crew", "orchestration", "multi-agent", "role-playing"],
    iconType: "agent",
    defaultTargetType: "crewai",
    defaultEndpoint: "file://./crewai_agent.py",
    defaultAuthType: "none",
    defaultAppTestingMode: "application",
  },
  {
    id: "google-adk",
    name: "Google ADK",
    description: "Google Agent Development Kit (ADK) modular multi-agent framework",
    badge: "Agent",
    categories: ["all", "agent-frameworks"],
    searchKeywords: ["google", "adk", "agent", "gemini", "genai", "google-adk", "google-genai"],
    iconType: "google",
    defaultTargetType: "google-adk",
    defaultEndpoint: "file://./google_adk_agent.py",
    defaultAuthType: "none",
    defaultAppTestingMode: "application",
  },
  {
    id: "langchain",
    name: "LangChain",
    description: "Test LangChain Chains & Agents",
    badge: "Agent",
    categories: ["all", "agent-frameworks"],
    searchKeywords: ["langchain", "agent", "chain", "framework", "python"],
    iconType: "langchain",
    defaultTargetType: "langchain",
    defaultEndpoint: "langchain://local_agent",
    defaultAppTestingMode: "application",
  },
  {
    id: "langgraph",
    name: "LangGraph Agent",
    description: "Cyclic stateful multi-agent workflows and graph orchestration",
    badge: "Agent",
    categories: ["all", "agent-frameworks"],
    searchKeywords: ["langgraph", "graph", "stateful", "cyclic", "agent", "langchain"],
    iconType: "langchain",
    defaultTargetType: "langgraph",
    defaultEndpoint: "file://./langgraph_agent.py",
    defaultAuthType: "none",
    defaultAppTestingMode: "application",
  },
  {
    id: "llamaindex",
    name: "LlamaIndex Agent",
    description: "Data framework for LLM applications and agentic query engines",
    badge: "Agent",
    categories: ["all", "agent-frameworks"],
    searchKeywords: ["llamaindex", "rag", "data", "agents", "index", "llama-index"],
    iconType: "agent",
    defaultTargetType: "llamaindex",
    defaultEndpoint: "file://./llamaindex_agent.py",
    defaultAuthType: "none",
    defaultAppTestingMode: "application",
  },
  {
    id: "mcp",
    name: "MCP Server",
    description: "Model Context Protocol servers & tools",
    categories: ["all", "agent-frameworks"],
    searchKeywords: ["mcp", "model context protocol", "server", "tools"],
    iconType: "agent",
    defaultTargetType: "mcp",
    defaultEndpoint: "http://localhost:8000/mcp",
    defaultAppTestingMode: "application",
  },
  {
    id: "open-interpreter",
    name: "Open Interpreter",
    description: "Local code execution interpreter",
    categories: ["all", "agent-frameworks"],
    searchKeywords: ["open interpreter", "code", "terminal", "interpreter"],
    iconType: "code",
    defaultTargetType: "open-interpreter",
    defaultEndpoint: "http://localhost:8000/interpreter",
    defaultAppTestingMode: "application",
  },
  {
    id: "pydantic-ai",
    name: "PydanticAI",
    description: "Agent framework with Pydantic validation",
    categories: ["all", "agent-frameworks"],
    searchKeywords: ["pydantic", "pydanticai", "agent", "validation"],
    iconType: "code",
    defaultTargetType: "http",
    defaultEndpoint: "http://localhost:8000/pydantic-ai",
    defaultAppTestingMode: "application",
  },
  {
    id: "other-agent",
    name: "Other Agent Framework",
    description: "Custom or third-party agent framework",
    categories: ["all", "agent-frameworks"],
    searchKeywords: ["agent", "custom", "framework", "third-party"],
    iconType: "agent",
    defaultTargetType: "other-agent",
    defaultEndpoint: "http://localhost:8000/agent/custom",
    defaultAppTestingMode: "application",
  },

  // ==========================================
  // 3. AI Providers (27 Targets)
  // ==========================================
  {
    id: "openai",
    name: "OpenAI",
    description: "GPT-4, GPT-3.5 and other models",
    badge: "Popular",
    categories: ["all", "ai-providers"],
    searchKeywords: ["openai", "gpt", "gpt-4", "gpt-4o", "gpt-3.5", "chatgpt", "o1", "o3", "hosted"],
    iconType: "openai",
    defaultTargetType: "ai_provider",
    defaultModelProvider: "openai",
    defaultModelName: "gpt-4o",
    defaultModelBaseUrl: "https://api.openai.com/v1",
    defaultEndpoint: "https://api.openai.com/v1/chat/completions",
    defaultAuthType: "bearer",
    defaultBodyTemplate: '{\n  "model": "gpt-4o",\n  "messages": [\n    {"role": "user", "content": "{{prompt}}"}\n  ]\n}',
    defaultResponsePath: "choices[0].message.content",
    defaultAppTestingMode: "model",
  },
  {
    id: "anthropic",
    name: "Anthropic",
    description: "Claude, Opus, and Haiku models",
    badge: "Popular",
    categories: ["all", "ai-providers"],
    searchKeywords: ["anthropic", "claude", "opus", "sonnet", "haiku", "llm", "hosted"],
    iconType: "anthropic",
    defaultTargetType: "ai_provider",
    defaultModelProvider: "anthropic",
    defaultModelName: "claude-3-5-sonnet-20241022",
    defaultModelBaseUrl: "https://api.anthropic.com/v1",
    defaultEndpoint: "https://api.anthropic.com/v1/messages",
    defaultAuthType: "api_key",
    defaultApiKeyLoc: "header",
    defaultApiKeyName: "x-api-key",
    defaultHeaders: { "anthropic-version": "2023-06-01" },
    defaultBodyTemplate: '{\n  "model": "claude-3-5-sonnet-20241022",\n  "max_tokens": 1024,\n  "messages": [\n    {"role": "user", "content": "{{prompt}}"}\n  ]\n}',
    defaultResponsePath: "content[0].text",
    defaultAppTestingMode: "model",
  },
  {
    id: "google",
    name: "Google AI Studio",
    description: "Gemini models via Google AI Studio",
    badge: "Popular",
    categories: ["all", "ai-providers"],
    searchKeywords: ["google", "gemini", "ai studio", "flash", "pro", "1.5", "2.0", "hosted"],
    iconType: "google",
    defaultTargetType: "ai_provider",
    defaultModelProvider: "google",
    defaultModelName: "gemini-1.5-pro",
    defaultModelBaseUrl: "https://generativelanguage.googleapis.com/v1beta",
    defaultEndpoint: "https://generativelanguage.googleapis.com/v1beta/openai/chat/completions",
    defaultAuthType: "bearer",
    defaultBodyTemplate: '{\n  "model": "gemini-1.5-pro",\n  "messages": [\n    {"role": "user", "content": "{{prompt}}"}\n  ]\n}',
    defaultResponsePath: "choices[0].message.content",
    defaultAppTestingMode: "model",
  },
  {
    id: "aiml-api",
    name: "AI/ML API",
    description: "Unified AI/ML API gateway",
    categories: ["all", "ai-providers"],
    searchKeywords: ["aiml", "api", "gateway", "unified"],
    iconType: "http",
    defaultTargetType: "ai_provider",
    defaultModelProvider: "aiml-api",
    defaultModelName: "default",
    defaultModelBaseUrl: "https://api.aimlapi.com/v1",
    defaultEndpoint: "https://api.aimlapi.com/v1/chat/completions",
    defaultAuthType: "bearer",
    defaultAppTestingMode: "model",
  },
  {
    id: "ai21",
    name: "AI21 Labs",
    description: "Jurassic and Jamba models",
    categories: ["all", "ai-providers"],
    searchKeywords: ["ai21", "jamba", "jurassic"],
    iconType: "http",
    defaultTargetType: "ai_provider",
    defaultModelProvider: "ai21",
    defaultModelName: "jamba-1.5-large",
    defaultModelBaseUrl: "https://api.ai21.com/studio/v1",
    defaultEndpoint: "https://api.ai21.com/studio/v1/chat/completions",
    defaultAuthType: "bearer",
    defaultAppTestingMode: "model",
  },
  {
    id: "sagemaker",
    name: "Amazon SageMaker",
    description: "Models hosted on AWS SageMaker",
    categories: ["all", "ai-providers"],
    searchKeywords: ["sagemaker", "aws", "amazon", "cloud"],
    iconType: "aws",
    defaultTargetType: "ai_provider",
    defaultModelProvider: "bedrock",
    defaultEndpoint: "https://runtime.sagemaker.us-east-1.amazonaws.com",
    defaultAuthType: "none",
    defaultAppTestingMode: "model",
  },
  {
    id: "bedrock",
    name: "AWS Bedrock",
    description: "Managed Foundation Models on Bedrock",
    categories: ["all", "ai-providers"],
    searchKeywords: ["bedrock", "aws", "amazon", "claude", "titan"],
    iconType: "aws",
    defaultTargetType: "ai_provider",
    defaultModelProvider: "bedrock",
    defaultModelName: "anthropic.claude-3-5-sonnet-20240620-v1:0",
    defaultEndpoint: "https://bedrock-runtime.us-east-1.amazonaws.com",
    defaultAuthType: "none",
    defaultAppTestingMode: "model",
  },
  {
    id: "azure",
    name: "Azure OpenAI",
    description: "Hosted OpenAI models on Azure",
    badge: "Popular",
    categories: ["all", "ai-providers"],
    searchKeywords: ["azure", "microsoft", "azure openai", "enterprise", "hosted"],
    iconType: "azure",
    defaultTargetType: "ai_provider",
    defaultModelProvider: "azure",
    defaultModelName: "gpt-4o",
    defaultModelBaseUrl: "https://YOUR_RESOURCE_NAME.openai.azure.com/",
    defaultEndpoint: "https://YOUR_RESOURCE_NAME.openai.azure.com/openai/deployments/gpt-4o/chat/completions?api-version=2024-02-15-preview",
    defaultAuthType: "api_key",
    defaultApiKeyLoc: "header",
    defaultApiKeyName: "api-key",
    defaultBodyTemplate: '{\n  "messages": [\n    {"role": "user", "content": "{{prompt}}"}\n  ]\n}',
    defaultResponsePath: "choices[0].message.content",
    defaultAppTestingMode: "model",
  },
  {
    id: "cerebras",
    name: "Cerebras",
    description: "Ultra-fast inference on Cerebras wafer-scale engine",
    categories: ["all", "ai-providers"],
    searchKeywords: ["cerebras", "llama", "fast", "hardware"],
    iconType: "http",
    defaultTargetType: "ai_provider",
    defaultModelProvider: "cerebras",
    defaultModelName: "llama3.1-8b",
    defaultModelBaseUrl: "https://api.cerebras.ai/v1",
    defaultEndpoint: "https://api.cerebras.ai/v1/chat/completions",
    defaultAuthType: "bearer",
    defaultAppTestingMode: "model",
  },
  {
    id: "cloudflare",
    name: "Cloudflare AI",
    description: "Workers AI serverless models",
    categories: ["all", "ai-providers"],
    searchKeywords: ["cloudflare", "workers", "serverless", "ai"],
    iconType: "http",
    defaultTargetType: "ai_provider",
    defaultModelProvider: "cloudflare",
    defaultEndpoint: "https://api.cloudflare.com/client/v4/accounts/ACCOUNT_ID/ai/run/@cf/meta/llama-3-8b-instruct",
    defaultAuthType: "bearer",
    defaultAppTestingMode: "model",
  },
  {
    id: "cohere",
    name: "Cohere",
    description: "Command R+, Command R, and Embed models",
    categories: ["all", "ai-providers"],
    searchKeywords: ["cohere", "command", "command-r", "embed"],
    iconType: "http",
    defaultTargetType: "ai_provider",
    defaultModelProvider: "cohere",
    defaultModelName: "command-r-plus",
    defaultModelBaseUrl: "https://api.cohere.com/v2",
    defaultEndpoint: "https://api.cohere.com/v2/chat",
    defaultAuthType: "bearer",
    defaultAppTestingMode: "model",
  },
  {
    id: "databricks",
    name: "Databricks",
    description: "Databricks Mosaic AI Model Serving",
    categories: ["all", "ai-providers"],
    searchKeywords: ["databricks", "mosaic", "dbrx", "serving"],
    iconType: "http",
    defaultTargetType: "ai_provider",
    defaultModelProvider: "databricks",
    defaultEndpoint: "https://dbc-workspace.cloud.databricks.com/serving-endpoints/dbrx-instruct/invocations",
    defaultAuthType: "bearer",
    defaultAppTestingMode: "model",
  },
  {
    id: "deepseek",
    name: "DeepSeek",
    description: "DeepSeek V3, R1, and coder models",
    categories: ["all", "ai-providers"],
    searchKeywords: ["deepseek", "v3", "r1", "coder", "moe"],
    iconType: "http",
    defaultTargetType: "ai_provider",
    defaultModelProvider: "deepseek",
    defaultModelName: "deepseek-chat",
    defaultModelBaseUrl: "https://api.deepseek.com/v1",
    defaultEndpoint: "https://api.deepseek.com/v1/chat/completions",
    defaultAuthType: "bearer",
    defaultAppTestingMode: "model",
  },
  {
    id: "fal",
    name: "fal.ai",
    description: "Fast generative media and multimodal models",
    categories: ["all", "ai-providers"],
    searchKeywords: ["fal", "fal.ai", "multimodal", "fast"],
    iconType: "http",
    defaultTargetType: "ai_provider",
    defaultModelProvider: "fal",
    defaultEndpoint: "https://queue.fal.run",
    defaultAuthType: "bearer",
    defaultAppTestingMode: "model",
  },
  {
    id: "fireworks",
    name: "Fireworks AI",
    description: "High-speed generative AI platform",
    categories: ["all", "ai-providers"],
    searchKeywords: ["fireworks", "fireworks.ai", "fast", "inference"],
    iconType: "http",
    defaultTargetType: "ai_provider",
    defaultModelProvider: "fireworks",
    defaultModelName: "accounts/fireworks/models/llama-v3p3-70b-instruct",
    defaultModelBaseUrl: "https://api.fireworks.ai/inference/v1",
    defaultEndpoint: "https://api.fireworks.ai/inference/v1/chat/completions",
    defaultAuthType: "bearer",
    defaultAppTestingMode: "model",
  },
  {
    id: "github-models",
    name: "GitHub Models",
    description: "Prototyping with models on GitHub",
    categories: ["all", "ai-providers"],
    searchKeywords: ["github", "models", "marketplace", "copilot"],
    iconType: "code",
    defaultTargetType: "ai_provider",
    defaultModelProvider: "github-models",
    defaultEndpoint: "https://models.inference.ai.azure.com/chat/completions",
    defaultAuthType: "bearer",
    defaultAppTestingMode: "model",
  },
  {
    id: "vertex",
    name: "Google Vertex AI",
    description: "Enterprise Gemini and PaLM on GCP",
    categories: ["all", "ai-providers"],
    searchKeywords: ["vertex", "gcp", "google cloud", "enterprise", "gemini"],
    iconType: "google",
    defaultTargetType: "ai_provider",
    defaultModelProvider: "google",
    defaultEndpoint: "https://us-central1-aiplatform.googleapis.com/v1/projects/PROJECT/locations/us-central1/publishers/google/models/gemini-1.5-pro:generateContent",
    defaultAuthType: "bearer",
    defaultAppTestingMode: "model",
  },
  {
    id: "groq",
    name: "Groq",
    description: "Ultra-fast LPU inference engine",
    categories: ["all", "ai-providers"],
    searchKeywords: ["groq", "lpu", "fast", "llama", "mixtral"],
    iconType: "http",
    defaultTargetType: "ai_provider",
    defaultModelProvider: "groq",
    defaultModelName: "llama-3.3-70b-versatile",
    defaultModelBaseUrl: "https://api.groq.com/openai/v1",
    defaultEndpoint: "https://api.groq.com/openai/v1/chat/completions",
    defaultAuthType: "bearer",
    defaultAppTestingMode: "model",
  },
  {
    id: "huggingface",
    name: "Hugging Face",
    description: "Inference Endpoints and Hub models",
    categories: ["all", "ai-providers"],
    searchKeywords: ["hugging face", "hf", "transformers", "endpoints"],
    iconType: "http",
    defaultTargetType: "ai_provider",
    defaultModelProvider: "huggingface",
    defaultModelName: "meta-llama/Llama-3.1-8B-Instruct",
    defaultModelBaseUrl: "https://api-inference.huggingface.co/v1",
    defaultEndpoint: "https://api-inference.huggingface.co/v1/chat/completions",
    defaultAuthType: "bearer",
    defaultAppTestingMode: "model",
  },
  {
    id: "hyperbolic",
    name: "Hyperbolic",
    description: "Decentralized open-access AI compute",
    categories: ["all", "ai-providers"],
    searchKeywords: ["hyperbolic", "decentralized", "gpu", "inference"],
    iconType: "http",
    defaultTargetType: "ai_provider",
    defaultModelProvider: "hyperbolic",
    defaultEndpoint: "https://api.hyperbolic.xyz/v1/chat/completions",
    defaultAuthType: "bearer",
    defaultAppTestingMode: "model",
  },
  {
    id: "mistral",
    name: "Mistral AI",
    description: "Mistral Large, Pixtral, and Codestral",
    categories: ["all", "ai-providers"],
    searchKeywords: ["mistral", "codestral", "pixtral", "large"],
    iconType: "http",
    defaultTargetType: "ai_provider",
    defaultModelProvider: "mistral",
    defaultModelName: "mistral-large-latest",
    defaultModelBaseUrl: "https://api.mistral.ai/v1",
    defaultEndpoint: "https://api.mistral.ai/v1/chat/completions",
    defaultAuthType: "bearer",
    defaultAppTestingMode: "model",
  },
  {
    id: "openrouter",
    name: "OpenRouter",
    description: "Unified routing for hosted LLMs",
    categories: ["all", "ai-providers"],
    searchKeywords: ["openrouter", "router", "unified", "aggregator"],
    iconType: "http",
    defaultTargetType: "ai_provider",
    defaultModelProvider: "openrouter",
    defaultModelName: "openai/gpt-4o",
    defaultModelBaseUrl: "https://openrouter.ai/api/v1",
    defaultEndpoint: "https://openrouter.ai/api/v1/chat/completions",
    defaultAuthType: "bearer",
    defaultAppTestingMode: "model",
  },
  {
    id: "perplexity",
    name: "Perplexity AI",
    description: "Sonar search and reasoning models",
    categories: ["all", "ai-providers"],
    searchKeywords: ["perplexity", "sonar", "search", "online"],
    iconType: "http",
    defaultTargetType: "ai_provider",
    defaultModelProvider: "perplexity",
    defaultModelName: "sonar-pro",
    defaultModelBaseUrl: "https://api.perplexity.ai",
    defaultEndpoint: "https://api.perplexity.ai/chat/completions",
    defaultAuthType: "bearer",
    defaultAppTestingMode: "model",
  },
  {
    id: "replicate",
    name: "Replicate",
    description: "Cloud model hosting and inference",
    categories: ["all", "ai-providers"],
    searchKeywords: ["replicate", "cloud", "api", "cog"],
    iconType: "http",
    defaultTargetType: "ai_provider",
    defaultModelProvider: "replicate",
    defaultEndpoint: "https://api.replicate.com/v1/predictions",
    defaultAuthType: "bearer",
    defaultAppTestingMode: "model",
  },
  {
    id: "together",
    name: "Together AI",
    description: "Open source model inference cloud",
    categories: ["all", "ai-providers"],
    searchKeywords: ["together", "together.ai", "open source", "inference"],
    iconType: "http",
    defaultTargetType: "ai_provider",
    defaultModelProvider: "together",
    defaultModelName: "meta-llama/Llama-3.3-70B-Instruct-Turbo",
    defaultModelBaseUrl: "https://api.together.xyz/v1",
    defaultEndpoint: "https://api.together.xyz/v1/chat/completions",
    defaultAuthType: "bearer",
    defaultAppTestingMode: "model",
  },
  {
    id: "voyage",
    name: "Voyage AI",
    description: "State-of-the-art embedding models",
    categories: ["all", "ai-providers"],
    searchKeywords: ["voyage", "embeddings", "retrieval", "rag"],
    iconType: "http",
    defaultTargetType: "ai_provider",
    defaultModelProvider: "voyage",
    defaultEndpoint: "https://api.voyageai.com/v1/embeddings",
    defaultAuthType: "bearer",
    defaultAppTestingMode: "model",
  },
  {
    id: "grok",
    name: "X.AI (Grok)",
    description: "Grok models by xAI",
    categories: ["all", "ai-providers"],
    searchKeywords: ["x.ai", "xai", "grok", "twitter"],
    iconType: "http",
    defaultTargetType: "ai_provider",
    defaultModelProvider: "xai",
    defaultModelName: "grok-beta",
    defaultModelBaseUrl: "https://api.x.ai/v1",
    defaultEndpoint: "https://api.x.ai/v1/chat/completions",
    defaultAuthType: "bearer",
    defaultAppTestingMode: "model",
  },

  // ==========================================
  // 4. Local Models (6 Targets)
  // ==========================================
  {
    id: "ollama",
    name: "Ollama",
    description: "Run open source models locally",
    badge: "Local",
    categories: ["all", "local-models"],
    searchKeywords: ["ollama", "local", "llama", "llama3", "mistral", "qwen", "phi", "self-hosted"],
    iconType: "ollama",
    defaultTargetType: "ollama",
    defaultModelProvider: "ollama",
    defaultModelName: "ollama:llama3",
    defaultModelBaseUrl: "http://localhost:11434",
    defaultEndpoint: "http://localhost:11434",
    defaultAuthType: "none",
    defaultBodyTemplate: '{\n  "model": "llama3",\n  "messages": [\n    {"role": "user", "content": "{{prompt}}"}\n  ]\n}',
    defaultResponsePath: "message.content",
    defaultAppTestingMode: "model",
  },
  {
    id: "llama-cpp",
    name: "llama.cpp",
    description: "Efficient C/C++ local model inference",
    badge: "Local",
    categories: ["all", "local-models"],
    searchKeywords: ["llama.cpp", "gguf", "c++", "local", "server", "llamacpp"],
    iconType: "ollama",
    defaultTargetType: "llamacpp",
    defaultModelProvider: "llamacpp",
    defaultModelName: "default",
    defaultModelBaseUrl: "http://localhost:8080",
    defaultEndpoint: "llama.cpp:http://localhost:8080/completion",
    defaultAuthType: "none",
    defaultAppTestingMode: "model",
  },
  {
    id: "llamafile",
    name: "Llamafile",
    description: "Single-file executable local models",
    badge: "Local",
    categories: ["all", "local-models"],
    searchKeywords: ["llamafile", "executable", "single file", "local", "openai-compatible"],
    iconType: "ollama",
    defaultTargetType: "llamafile",
    defaultModelProvider: "llamafile",
    defaultModelName: "LLaMA_CPP",
    defaultModelBaseUrl: "http://localhost:8080/v1",
    defaultEndpoint: "http://localhost:8080/v1/chat/completions",
    defaultAuthType: "none",
    defaultAppTestingMode: "model",
  },
  {
    id: "localai",
    name: "LocalAI",
    description: "Self-hosted local OpenAI-compatible API",
    badge: "Local",
    categories: ["all", "local-models"],
    searchKeywords: ["localai", "self-hosted", "local", "drop-in", "openai-compatible"],
    iconType: "vllm",
    defaultTargetType: "localai",
    defaultModelProvider: "localai",
    defaultModelName: "gpt-4",
    defaultModelBaseUrl: "http://localhost:8080/v1",
    defaultEndpoint: "http://localhost:8080/v1/chat/completions",
    defaultAuthType: "none",
    defaultAppTestingMode: "model",
  },
  {
    id: "text-generation-webui",
    name: "Text Generation WebUI",
    description: "Gradio web UI for local models",
    badge: "Local",
    categories: ["all", "local-models"],
    searchKeywords: ["oobabooga", "text-generation-webui", "gradio", "local", "openai-compatible"],
    iconType: "lmstudio",
    defaultTargetType: "text-generation-webui",
    defaultModelProvider: "text-generation-webui",
    defaultModelName: "default",
    defaultModelBaseUrl: "http://localhost:5000/v1",
    defaultEndpoint: "http://localhost:5000/v1/chat/completions",
    defaultAuthType: "none",
    defaultAppTestingMode: "model",
  },
  {
    id: "vllm",
    name: "vLLM",
    description: "High-performance local model serving",
    badge: "Local",
    categories: ["all", "local-models"],
    searchKeywords: ["vllm", "local", "gpu", "inference", "serving", "self-hosted", "openai-compatible"],
    iconType: "vllm",
    defaultTargetType: "vllm",
    defaultModelProvider: "vllm",
    defaultModelName: "meta-llama/Llama-2-7b-chat-hf",
    defaultModelBaseUrl: "http://localhost:8000/v1",
    defaultEndpoint: "http://localhost:8000/v1/chat/completions",
    defaultAuthType: "none",
    defaultBodyTemplate: '{\n  "model": "meta-llama/Llama-2-7b-chat-hf",\n  "messages": [\n    {"role": "user", "content": "{{prompt}}"}\n  ]\n}',
    defaultResponsePath: "choices[0].message.content",
    defaultAppTestingMode: "model",
  },
];

/**
 * Unified target model representing either a catalog item or a user-configured target.
 */
export interface TargetCardModel {
  id: string;
  name: string;
  description: string;
  badge?: TargetBadgeType;
  categories: TargetCategoryId[];
  searchKeywords: string[];
  iconType: string;
  isSavedTarget: boolean;
  savedTargetData?: Target;
  catalogItem?: TargetCatalogItem;
}

/**
 * Validates whether a target record is eligible as an application target.
 */
export function isEligibleApplicationTarget(target: unknown): boolean {
  if (!target || typeof target !== "object") {
    return false;
  }
  const t = target as Record<string, unknown>;

  if (!t.id && (t.scan_id || t.trace_id || t.run_id)) {
    return false;
  }

  const endpoint = typeof t.endpoint === "string" ? t.endpoint.trim() : "";
  if (!endpoint) {
    return false;
  }

  const lowerEndpoint = endpoint.toLowerCase();
  const lowerType = typeof t.type === "string" ? t.type.toLowerCase() : "";
  const isHttp = lowerEndpoint.startsWith("http://") || lowerEndpoint.startsWith("https://");
  const isWs =
    lowerEndpoint.startsWith("ws://") ||
    lowerEndpoint.startsWith("wss://") ||
    lowerType.includes("websocket") ||
    lowerType === "ws";

  if (!isHttp && !isWs) {
    return false;
  }

  const name = typeof t.name === "string" ? t.name.trim() : "";
  const isPlaceholderName = !name || name.toLowerCase() === "new http target";
  const isPlaceholderEndpoint =
    lowerEndpoint.includes("api.example.com") || lowerEndpoint === "https://api.example.com/v1/chat";
  if (isPlaceholderName && isPlaceholderEndpoint) {
    return false;
  }

  return true;
}

export function normalizeEndpoint(url: string): string {
  try {
    const trimmed = url.trim();
    const parsed = new URL(trimmed);
    const host = parsed.hostname.toLowerCase();
    const port = parsed.port ? `:${parsed.port}` : "";
    const pathname = parsed.pathname.replace(/\/+$/, "");
    return `${parsed.protocol.toLowerCase()}//${host}${port}${pathname}`;
  } catch {
    return url.trim().toLowerCase().replace(/\/+$/, "");
  }
}

export function deduplicateApplicationTargets(targets: Target[]): Target[] {
  const byKey = new Map<string, Target>();

  for (const t of targets) {
    if (!isEligibleApplicationTarget(t)) {
      continue;
    }

    const key = normalizeEndpoint(t.endpoint || "");
    if (!byKey.has(key)) {
      byKey.set(key, { ...t });
    } else {
      const existing = byKey.get(key)!;
      const existingNameIsGeneric =
        !existing.name ||
        existing.name.toLowerCase().startsWith("test") ||
        existing.name.toLowerCase().includes("new http target");
      const currentNameIsGeneric =
        !t.name ||
        t.name.toLowerCase().startsWith("test") ||
        t.name.toLowerCase().includes("new http target");

      const preferCurrent =
        (existingNameIsGeneric && !currentNameIsGeneric) ||
        (existingNameIsGeneric === currentNameIsGeneric && t.id > existing.id);

      if (preferCurrent) {
        const merged: Target = {
          ...t,
          last_scan_id: t.last_scan_id || existing.last_scan_id,
          last_scan_score: t.last_scan_score ?? existing.last_scan_score,
          last_scan_status: t.last_scan_status || existing.last_scan_status,
          last_scan_date: t.last_scan_date || existing.last_scan_date,
          last_scan_summary: t.last_scan_summary || existing.last_scan_summary,
        };
        byKey.set(key, merged);
      } else {
        if (t.last_scan_id && !existing.last_scan_id) {
          existing.last_scan_id = t.last_scan_id;
          existing.last_scan_score = t.last_scan_score;
          existing.last_scan_status = t.last_scan_status;
          existing.last_scan_date = t.last_scan_date;
          existing.last_scan_summary = t.last_scan_summary;
        }
      }
    }
  }

  return Array.from(byKey.values());
}

/**
 * Builds the complete unified list of target cards.
 * Executed application scan runs are excluded from target selection cards
 * per user requirements; instead, the 55 architecture target types are rendered.
 */
export function buildUnifiedTargetCards(_savedTargets?: Target[]): TargetCardModel[] {
  return TARGET_CATALOG.map((item) => ({
    id: item.id,
    name: item.name,
    description: item.description,
    badge: item.badge,
    categories: item.categories,
    searchKeywords: item.searchKeywords,
    iconType: item.iconType,
    isSavedTarget: false,
    catalogItem: item,
  }));
}

/**
 * Calculates dynamic counts for all categories from actual catalog items.
 * Guaranteed to match:
 * - all: 55
 * - my-applications: 8
 * - agent-frameworks: 14
 * - ai-providers: 27
 * - local-models: 6
 */
export function calculateCategoryCounts(): Record<TargetCategoryId, number> {
  const counts: Record<TargetCategoryId, number> = {
    all: TARGET_CATALOG.length,
    "my-applications": TARGET_CATALOG.filter((i) => i.categories.includes("my-applications")).length,
    "agent-frameworks": TARGET_CATALOG.filter((i) => i.categories.includes("agent-frameworks")).length,
    "ai-providers": TARGET_CATALOG.filter((i) => i.categories.includes("ai-providers")).length,
    "local-models": TARGET_CATALOG.filter((i) => i.categories.includes("local-models")).length,
  };
  return counts;
}

/**
 * Filters target cards by category and search keyword.
 * Search and category filtering work together.
 */
export function filterTargetCards(
  cards: TargetCardModel[],
  activeCategory: TargetCategoryId,
  searchQuery: string
): TargetCardModel[] {
  const normalizedQuery = searchQuery.trim().toLowerCase();

  return cards.filter((card) => {
    // 1. Category check
    const matchesCategory =
      activeCategory === "all"
        ? true
        : card.categories.includes(activeCategory);

    if (!matchesCategory) {
      return false;
    }

    // 2. Search check
    if (!normalizedQuery) {
      return true;
    }

    const matchesName = card.name.toLowerCase().includes(normalizedQuery);
    const matchesDesc = card.description.toLowerCase().includes(normalizedQuery);
    const matchesKeywords = card.searchKeywords.some((kw) =>
      kw.toLowerCase().includes(normalizedQuery)
    );

    return matchesName || matchesDesc || matchesKeywords;
  });
}
